import { GlobeFillIcon } from '@zep-us/zus';
import { Blue500, ICON_COLOR } from '@zep-us/zus/tokens';
import type { WebsiteData } from '@/api/ai/website/types';
import type { YoutubeVideoData } from '@/api/ai/youtube/types';
import { XSmallIcon } from '@/icons';
import { Button, type ButtonProps } from '@/ui_v3';
import { cn } from '@/utils';
import { getFileTypeImage } from './fileTypeImages';
const ICON_SIZE = 15; // Figma 디자인 명세에 맞춘 15px

/**
 * 파일 타입 아이콘 컴포넌트
 */
function FileTypeIcon({
  filename
}: {
  filename: string;
}) {
  const imageSrc = getFileTypeImage(filename);
  if (!imageSrc) {
    return null;
  }
  return <img src={imageSrc} alt={filename} width={ICON_SIZE} height={ICON_SIZE} className="object-contain" data-sentry-component="FileTypeIcon" data-sentry-source-file="AttachmentButton.tsx" />;
}

/**
 * YouTube 썸네일 컴포넌트
 */
function YoutubeThumbnail({
  videoData
}: {
  videoData: YoutubeVideoData;
}) {
  return <img src={videoData.thumbnailUrl} alt={videoData.title} className="size-[20px] rounded-2xs object-cover" onError={e => {
    e.currentTarget.style.display = 'none';
  }} data-sentry-component="YoutubeThumbnail" data-sentry-source-file="AttachmentButton.tsx" />;
}

/**
 * 그라디언트 보더 래퍼
 * 위에서 아래로 갈수록 진해지는 violet 색상의 보더와 그림자 효과를 제공합니다
 */
function GradientBorderWrapper({
  children
}: {
  children: React.ReactNode;
}) {
  return <div className="relative inline-block rounded-full shadow-[0px_3px_6px_0px_#e7e4ff,0px_0px_10px_0px_#ebdbff]" data-sentry-component="GradientBorderWrapper" data-sentry-source-file="AttachmentButton.tsx">
      {/* 그라디언트 보더를 위한 배경 레이어 */}
      <div className="absolute inset-0 rounded-full bg-gradient-to-b from-violet-100 to-violet-300 p-[1px]">
        <div className="size-full rounded-full bg-gray-50" />
      </div>

      {/* 실제 컨텐츠 */}
      <div className="relative">{children}</div>
    </div>;
}

/**
 * AttachmentButton 베이스 컴포넌트
 */
const AttachmentButtonBase = (props: Omit<ButtonProps, 'asChild'> & {
  leftSlot?: React.ReactNode;
  onDelete?: () => void;
}) => {
  const {
    className,
    children,
    leftSlot,
    onDelete,
    ...rest
  } = props;
  return <GradientBorderWrapper data-sentry-element="GradientBorderWrapper" data-sentry-component="AttachmentButtonBase" data-sentry-source-file="AttachmentButton.tsx">
      <Button variant="contained" intent="neutral" size="md" rounded="full" className={cn('relative bg-transparent text-body-md-semibold text-default', className)} leftSlot={leftSlot} rightSlot={onDelete && <button onClick={e => {
      e.stopPropagation();
      onDelete();
    }} className="flex size-5 items-center justify-center" aria-label="Remove attachment">
              <XSmallIcon size="sm" fill={ICON_COLOR.neutral} />
            </button>} {...rest} data-sentry-element="Button" data-sentry-source-file="AttachmentButton.tsx">
        <span className="max-w-[200px] truncate">{children}</span>
      </Button>
    </GradientBorderWrapper>;
};

/**
 * 파일 첨부 버튼
 */
export function FileAttachmentButton({
  file,
  onDelete,
  disabled,
  onClick
}: {
  file: File;
  onDelete?: () => void;
  disabled?: boolean;
  onClick?: () => void;
}) {
  return <AttachmentButtonBase disabled={disabled} leftSlot={<FileTypeIcon filename={file.name} />} onDelete={onDelete} onClick={onClick} data-sentry-element="AttachmentButtonBase" data-sentry-component="FileAttachmentButton" data-sentry-source-file="AttachmentButton.tsx">
      {file.name}
    </AttachmentButtonBase>;
}

/**
 * YouTube 첨부 버튼
 */
export function YoutubeAttachmentButton({
  videoData,
  onDelete,
  onClick,
  disabled
}: {
  videoData: YoutubeVideoData;
  onDelete?: () => void;
  onClick?: () => void;
  disabled?: boolean;
}) {
  return <AttachmentButtonBase disabled={disabled} leftSlot={<YoutubeThumbnail videoData={videoData} />} onDelete={onDelete} onClick={onClick} data-sentry-element="AttachmentButtonBase" data-sentry-component="YoutubeAttachmentButton" data-sentry-source-file="AttachmentButton.tsx">
      {videoData.title}
    </AttachmentButtonBase>;
}

/**
 * Website 첨부 버튼
 */
export function WebsiteAttachmentButton({
  websiteData,
  onDelete,
  onClick,
  disabled
}: {
  websiteData: WebsiteData;
  onDelete?: () => void;
  onClick?: () => void;
  disabled?: boolean;
}) {
  return <AttachmentButtonBase disabled={disabled} leftSlot={<GlobeFillIcon size="xs" fill={Blue500} />} onDelete={onDelete} onClick={onClick} data-sentry-element="AttachmentButtonBase" data-sentry-component="WebsiteAttachmentButton" data-sentry-source-file="AttachmentButton.tsx">
      {websiteData.title}
    </AttachmentButtonBase>;
}

/**
 * 범용 첨부 버튼 (하위 호환성)
 *
 * FileAttachmentButton 또는 YoutubeAttachmentButton을 직접 사용하는 것을 권장합니다
 */
export interface AttachmentButtonProps {
  type: 'file' | 'youtube';
  attachment: File | YoutubeVideoData;
  onDelete?: () => void;
  disabled?: boolean;
}
export function AttachmentButton({
  type,
  attachment,
  onDelete,
  disabled
}: AttachmentButtonProps) {
  if (type === 'youtube') {
    return <YoutubeAttachmentButton disabled={disabled} videoData={attachment as YoutubeVideoData} onDelete={onDelete} />;
  }
  return <FileAttachmentButton disabled={disabled} file={attachment as File} onDelete={onDelete} data-sentry-element="FileAttachmentButton" data-sentry-component="AttachmentButton" data-sentry-source-file="AttachmentButton.tsx" />;
}